import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_CS = { title: "", slug: "", client_name: "", sector: "", problem: "", approach: "", outcome: "", published: false };

export default function AdminCaseStudies() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editCase, setEditCase] = useState(null);
  const [form, setForm] = useState(EMPTY_CS);
  const [saving, setSaving] = useState(false);

  const fetchCases = async () => {
    try {
      const { data } = await axios.get(`${API}/api/case-studies?published_only=false`);
      setCases(data);
    } catch { /* empty */ }
    setLoading(false);
  };

  useEffect(() => { fetchCases(); }, []);

  const openCreate = () => { setForm(EMPTY_CS); setEditCase("new"); };
  const openEdit = (cs) => { setForm({ ...EMPTY_CS, ...cs }); setEditCase(cs.id); };
  const close = () => { setEditCase(null); };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editCase === "new") {
        await axios.post(`${API}/api/case-studies`, form);
      } else {
        await axios.put(`${API}/api/case-studies/${editCase}`, form);
      }
      close();
      fetchCases();
    } catch { /* empty */ }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this case study?")) return;
    await axios.delete(`${API}/api/case-studies/${id}`);
    fetchCases();
  };

  const autoSlug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  return (
    <div data-testid="admin-case-studies-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-2xl text-navy-900">Case Studies</h1>
        <button onClick={openCreate} data-testid="create-case-study-button" className="flex items-center gap-2 bg-navy-900 text-white px-5 py-2.5 rounded-sm text-sm font-sans font-medium hover:bg-navy-800 transition-colors">
          <Plus className="w-4 h-4" /> New Case Study
        </button>
      </div>

      {loading ? (
        <p className="text-gray-400 font-sans text-sm">Loading...</p>
      ) : cases.length === 0 ? (
        <div className="text-center py-20 border border-gray-200 bg-white"><p className="text-gray-400 font-sans">No case studies yet.</p></div>
      ) : (
        <div className="space-y-2">
          {cases.map((cs) => (
            <div key={cs.id} data-testid={`case-study-item-${cs.id}`} className="bg-white border border-gray-200 p-4 flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-sans text-sm font-medium text-navy-900">{cs.title}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-sm ${cs.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {cs.published ? "Published" : "Draft"}
                  </span>
                </div>
                <p className="text-xs text-gray-400 font-sans mt-1">{cs.sector} | {cs.client_name}</p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => openEdit(cs)} className="p-2 text-gray-400 hover:text-navy-900 transition-colors"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(cs.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={editCase !== null} onOpenChange={(open) => { if (!open) close(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-navy-900">
              {editCase === "new" ? "Create Case Study" : "Edit Case Study"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: autoSlug(e.target.value) })} className="border-gray-200 rounded-sm h-10 font-sans" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Client Name</Label>
                <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="border-gray-200 rounded-sm h-10 font-sans" />
              </div>
              <div>
                <Label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Sector</Label>
                <Input value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} className="border-gray-200 rounded-sm h-10 font-sans" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Problem</Label>
              <Textarea value={form.problem} onChange={(e) => setForm({ ...form, problem: e.target.value })} className="border-gray-200 rounded-sm font-sans min-h-[80px]" />
            </div>
            <div>
              <Label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Approach</Label>
              <Textarea value={form.approach} onChange={(e) => setForm({ ...form, approach: e.target.value })} className="border-gray-200 rounded-sm font-sans min-h-[80px]" />
            </div>
            <div>
              <Label className="text-sm font-sans font-medium text-gray-700 mb-1 block">Outcome</Label>
              <Textarea value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} className="border-gray-200 rounded-sm font-sans min-h-[80px]" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="cs-published" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="rounded border-gray-300" />
              <Label htmlFor="cs-published" className="text-sm font-sans text-gray-700">Published</Label>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <button onClick={close} className="px-5 py-2.5 border border-gray-200 text-gray-600 rounded-sm text-sm font-sans hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={handleSave} data-testid="save-case-study-button" disabled={saving} className="px-5 py-2.5 bg-navy-900 text-white rounded-sm text-sm font-sans font-medium hover:bg-navy-800 transition-colors disabled:opacity-50">
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
